import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, Subsector } from '../services/api'
import { Plus, X, Edit2, Save, ChevronDown, ChevronRight, Minus } from 'lucide-react'

export default function Subsectors() {
  const queryClient = useQueryClient()
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [newName, setNewName] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['subsectors'],
    queryFn: () => api.getSubsectors(),
  })

  // Fetch latest snapshot to get underlying descriptions
  const { data: snapshots } = useQuery({
    queryKey: ['snapshots-latest'],
    queryFn: () => api.getSnapshots(0, 1),
  })

  const latestUploadId = snapshots?.uploads?.[0]?.id

  // Fetch aggregate portfolio to get stock descriptions
  const { data: aggregateData } = useQuery({
    queryKey: ['aggregate', latestUploadId],
    queryFn: () => api.getAggregate(latestUploadId!),
    enabled: !!latestUploadId,
  })

  // Build a map of symbol -> description from positions
  // First pass: get stock descriptions (preferred)
  // Second pass: for underlyings with only options, try to extract from option description
  const symbolDescriptions = useMemo(() => {
    const map: Record<string, string> = {}
    if (aggregateData?.positions) {
      // First pass: stocks (these have clean company names)
      for (const pos of aggregateData.positions) {
        if (pos.symbol && pos.description && pos.asset_category === 'Stocks') {
          map[pos.symbol] = pos.description
        }
      }
      // Second pass: options (for underlyings without stock positions)
      for (const pos of aggregateData.positions) {
        if (pos.symbol && pos.description && pos.asset_category === 'Equity and Index Options') {
          // Extract underlying from OCC symbol (first part before spaces/numbers)
          const match = pos.symbol.match(/^([A-Z]+)/)
          const underlying = match ? match[1] : null
          if (underlying && !map[underlying]) {
            // Option description format varies, try to extract company name
            // e.g., "CHARTER COMMUNICATIONS INC-A JAN 17 25 300 CALL"
            const descMatch = pos.description.match(/^(.+?)\s+(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)/i)
            if (descMatch) {
              map[underlying] = descMatch[1].trim()
            }
          }
        }
      }
    }
    return map
  }, [aggregateData])

  const createMutation = useMutation({
    mutationFn: (name: string) => api.createSubsector(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subsectors'] })
      setNewName('')
      setShowCreateForm(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      api.updateSubsector(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subsectors'] })
      setEditingId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deleteSubsector(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subsectors'] })
    },
  })

  const removeUnderlyingMutation = useMutation({
    mutationFn: ({ subsectorId, underlying }: { subsectorId: number; underlying: string }) =>
      api.removeUnderlyingFromSubsector(subsectorId, underlying),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subsectors'] })
    },
  })

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const startEditing = (subsector: Subsector) => {
    setEditingId(subsector.id)
    setEditName(subsector.name)
  }

  const saveEdit = (id: number) => {
    if (editName.trim()) {
      updateMutation.mutate({ id, name: editName.trim() })
    }
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditName('')
  }

  const handleCreate = () => {
    if (newName.trim()) {
      createMutation.mutate(newName.trim())
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Subsectors</h1>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          New Subsector
        </button>
      </div>

      <div className="card">
        <p className="text-sm text-gray-600 mb-6">
          Create subsectors to group underlyings for filtered exposure views on comparison pages.
          Add underlyings to subsectors from the comparison pages using the + button.
        </p>

        {/* Create form */}
        {showCreateForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Subsector name"
                className="input flex-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreate()
                  if (e.key === 'Escape') {
                    setShowCreateForm(false)
                    setNewName('')
                  }
                }}
              />
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || createMutation.isPending}
                className="btn btn-primary"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowCreateForm(false)
                  setNewName('')
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
            {createMutation.isError && (
              <p className="text-red-500 text-sm mt-2">
                {(createMutation.error as Error)?.message || 'Failed to create subsector'}
              </p>
            )}
          </div>
        )}

        {data?.subsectors.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">
              No subsectors yet. Click "New Subsector" to create one.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {data?.subsectors.map((subsector) => {
              const isExpanded = expandedIds.has(subsector.id)
              const isEditing = editingId === subsector.id

              return (
                <div key={subsector.id} className="border rounded-lg">
                  {/* Subsector header row */}
                  <div
                    className="flex items-center gap-2 p-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => !isEditing && toggleExpand(subsector.id)}
                  >
                    <div className="p-1">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </div>

                    {isEditing ? (
                      <div className="flex items-center gap-2 flex-1" onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="input flex-1"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit(subsector.id)
                            if (e.key === 'Escape') cancelEdit()
                          }}
                        />
                        <button
                          onClick={() => saveEdit(subsector.id)}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                          disabled={updateMutation.isPending}
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="font-medium text-gray-900 flex-1">
                          {subsector.name}
                        </span>
                        <span className="text-sm text-gray-500">
                          {subsector.underlyings.length} underlying{subsector.underlyings.length !== 1 ? 's' : ''}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); startEditing(subsector) }}
                          className="p-1 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded"
                          title="Rename"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (confirm(`Delete "${subsector.name}"? This cannot be undone.`)) {
                              deleteMutation.mutate(subsector.id)
                            }
                          }}
                          className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                          disabled={deleteMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Expanded underlyings list */}
                  {isExpanded && (
                    <div className="border-t bg-gray-50 px-4 py-2">
                      {subsector.underlyings.length === 0 ? (
                        <p className="text-sm text-gray-500 py-2">
                          No underlyings in this subsector. Add them from the comparison pages.
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {subsector.underlyings
                            .sort((a, b) => a.underlying.localeCompare(b.underlying))
                            .map((u) => {
                              const description = symbolDescriptions[u.underlying]
                              return (
                                <div
                                  key={u.id}
                                  className="flex items-center justify-between py-1 px-2 hover:bg-gray-100 rounded"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-mono font-medium">{u.underlying}</span>
                                    {description && (
                                      <span className="text-sm text-gray-500">{description}</span>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => removeUnderlyingMutation.mutate({
                                      subsectorId: subsector.id,
                                      underlying: u.underlying,
                                    })}
                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                    title="Remove from subsector"
                                    disabled={removeUnderlyingMutation.isPending}
                                  >
                                    <Minus className="h-3 w-3" />
                                  </button>
                                </div>
                              )
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Info card */}
      <div className="card bg-gray-50">
        <h3 className="font-medium text-gray-900 mb-2">About Subsectors</h3>
        <p className="text-sm text-gray-600">
          Subsectors let you group underlyings for analysis. On comparison pages, select a subsector
          from the dropdown to see a filtered table showing only those underlyings with recalculated
          totals and weighted averages.
        </p>
      </div>
    </div>
  )
}
